import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { XCircle, ArrowRight, RefreshCw } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const orderId = searchParams.get('order_id');
  const status = searchParams.get('status');
  const message = searchParams.get('message');
  const error = searchParams.get('error');
  
  useEffect(() => {
    // Clear pending booking data (legacy localStorage + Sprint-4 session token)
    localStorage.removeItem('pendingBooking');
    try {
      const tok = sessionStorage.getItem('paymentSessionToken');
      if (tok) {
        sessionStorage.removeItem('paymentSessionToken');
        axios.delete(`${BACKEND_URL}/api/payment/session/${encodeURIComponent(tok)}`).catch(() => {});
      }
    } catch { /* ignore */ }
  }, []);
  
  const retryPayment = () => {
    // Get the stored booking data if exists (either legacy or session)
    const pendingBooking = localStorage.getItem('pendingBooking');
    const sessionToken = sessionStorage.getItem('paymentSessionToken');
    
    if (pendingBooking || sessionToken) {
      // Navigate back to booking page to retry
      navigate('/booking', { replace: true });
    } else {
      // Start fresh
      navigate('/');
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Error Icon */}
          <div className="text-center mb-6">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-16 h-16 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="payment-cancel-header">Payment {status === 'Cancelled' ? 'Cancelled' : 'Failed'}</h1>
            <p className="text-gray-600">
              {message || error || 'Your payment could not be processed'}
            </p>
          </div>
          
          {/* Payment Details */}
          {orderId && (
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="grid gap-3">
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-bold text-lg text-gray-800">{orderId}</p>
                </div>
                {status && (
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className="font-bold text-lg text-red-600">{status}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Information */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <h3 className="font-bold text-yellow-800 mb-2">What happened?</h3>
            <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
              <li>Your payment was not completed</li>
              <li>No amount has been deducted from your account</li>
              <li>Your booking has been cancelled</li>
              <li>You can try again with a different payment method</li>
            </ul>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4">
            <button
              type="button"
              data-testid="payment-cancel-retry-btn"
              onClick={retryPayment}
              className="flex-1 py-4 px-6 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Retry Payment
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 py-4 px-6 bg-white border-2 border-[#1B375B] text-[#1B375B] rounded-xl font-bold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-5 h-5 rotate-180" />
              Search New Flight
            </button>
          </div>
          
          {/* Support Information */}
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-gray-600">
              If you believe this is an error or need assistance, please contact our support team
            </p>
            <p className="text-sm font-semibold text-[#1B375B] mt-2">
              support@acftourism.com | +91-1234567890
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentCancel;